/**
 * CommandPalette.jsx — Phase 2 App Shell (Jul 2026)
 *
 * Global ⌘K / Ctrl+K palette. Same result pool as the sidebar in-nav
 * search (filtered by role + active modules) and uses the SAME scored
 * ranking so `hol` → Holidays sits at the top instead of `Analytics`.
 * We disable cmdk's built-in filter with `shouldFilter={false}` and
 * pre-sort ourselves.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '../ui/command';
import { useModuleActivation } from './ModuleActivationContext';
import { iconForModule } from '../../config/moduleIcons';

// Mirror of AppSidebar.searchScore (kept in sync manually — small enough).
function score(label, groupLabel, q) {
  const n = (q || '').trim().toLowerCase();
  if (!n) return 1;                 // empty query → keep everything at neutral score
  const l = (label || '').toLowerCase();
  const g = (groupLabel || '').toLowerCase();
  if (l.startsWith(n)) return 100;
  if (l.includes(n))   return 50;
  if (g.startsWith(n)) return 30;
  if (g.includes(n))   return 20;
  let hi = 0;
  for (let i = 0; i < n.length; i++) {
    const idx = l.indexOf(n[i], hi);
    if (idx === -1) return -1;
    hi = idx + 1;
  }
  return 5;
}

export default function CommandPalette({ open, setOpen, user, platformItems = [] }) {
  const navigate = useNavigate();
  const { catalogGroups, activeKeys } = useModuleActivation();
  const [query, setQuery] = useState('');

  // Keyboard shortcut: ⌘K on mac, Ctrl+K elsewhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  // Pool of catalog results the user is allowed to see (role + active).
  const catalogItems = useMemo(() => {
    const out = [];
    for (const g of catalogGroups) {
      for (const m of (g.modules || [])) {
        if (Array.isArray(m.roles) && user?.role && !m.roles.includes(user.role)) continue;
        if (!activeKeys.has(m.key)) continue;
        out.push({
          key:        m.key,
          label:      m.label,
          groupKey:   g.groupKey,
          groupLabel: g.groupLabel,
          route:      m.route,
          Icon:       iconForModule(m.key),
        });
      }
    }
    return out;
  }, [catalogGroups, activeKeys, user?.role]);

  // Sorted + filtered by score.
  const results = useMemo(() => {
    const scored = [];
    for (const i of catalogItems) {
      const s = score(i.label, i.groupLabel, query);
      if (s > 0) scored.push({ ...i, _score: s });
    }
    // Higher score first; ties broken by catalog order (already stable).
    scored.sort((a, b) => b._score - a._score);
    return scored;
  }, [catalogItems, query]);

  const platformResults = useMemo(() => {
    if (!platformItems.length) return [];
    const scored = [];
    for (const p of platformItems) {
      const s = score(p.label, 'Platform', query);
      if (s > 0) scored.push({ ...p, _score: s });
    }
    scored.sort((a, b) => b._score - a._score);
    return scored;
  }, [platformItems, query]);

  const go = (path) => {
    setOpen(false);
    if (!path) return;
    if (/^https?:\/\//i.test(path) || path.startsWith('//')) window.open(path, '_blank');
    else navigate(path);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Search modules, pages… (⌘K)"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {platformResults.length > 0 && (
          <>
            <CommandGroup heading="Platform">
              {platformResults.map((p) => (
                <CommandItem
                  key={`platform:${p.path}`}
                  value={`platform-${p.path}`}
                  onSelect={() => go(p.path)}
                >
                  <span>{p.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {results.length > 0 && (
          <CommandGroup heading={query ? 'Results' : 'All modules'}>
            {results.map((i) => (
              <CommandItem
                key={`catalog:${i.key}`}
                value={`catalog-${i.key}`}
                onSelect={() => go(i.route)}
              >
                <i.Icon className="mr-2 h-4 w-4" />
                <span>{i.label}</span>
                <span className="ml-auto text-xs text-foreground-subtle">{i.groupLabel}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
